/**
 * supabase-js returns PostgrestError as a plain object, not an Error, so a
 * caller's `err instanceof Error ? err.message : "..."` fell back to a generic
 * message (2026-10-15 "Export failed"). Wrap it in a real Error that keeps the
 * PostgREST message and SQLSTATE `code` (e.g. 57014 = statement timeout).
 * 57014 is also raised by a manual cancel; the export treats both as "timed
 * out", which is what a user would see in practice.
 */
export function toRpcError(
  what: string,
  error: { message?: string; code?: string },
): Error & { code?: string } {
  // `cause` keeps the full PostgrestError (details, hint) for console logs.
  const e = new Error(`${what}: ${error.message || "unknown database error"}`, {
    cause: error,
  }) as Error & { code?: string };
  e.code = error.code;
  return e;
}

/**
 * Message for a failed export. Handles real Errors and raw PostgrestError-like
 * objects ({ message, code }); SQLSTATE 57014 (statement timeout) gets an
 * actionable message instead of the database text.
 */
export function exportErrorMessage(err: unknown): string {
  const e = err as { code?: unknown; message?: unknown } | null;
  if (e && e.code === "57014") {
    return "The export timed out. Narrow the filters and try again.";
  }
  if (e && typeof e.message === "string" && e.message.trim() !== "") {
    return `Export failed: ${e.message}`;
  }
  return "Export failed";
}

export const SEARCH_TIMEOUT_MESSAGE =
  "This search took too long — narrow the date range or filters and try again.";

/**
 * Message for a failed page load (e.g. Voucher History). Accepts real Errors
 * and raw PostgrestError-like objects, so the screen shows the database's own
 * text instead of "Unknown error"; a statement timeout (57014) gets an
 * actionable message. Also checks `cause`, so an Error built by toRpcError
 * keeps the timeout mapping.
 */
export function loadErrorMessage(err: unknown): string {
  const e = err as {
    code?: unknown;
    message?: unknown;
    cause?: { code?: unknown };
  } | null;
  if (e && (e.code === "57014" || e.cause?.code === "57014")) {
    return SEARCH_TIMEOUT_MESSAGE;
  }
  if (typeof err === "string" && err.trim() !== "") {
    return err;
  }
  if (e && typeof e.message === "string" && e.message.trim() !== "") {
    return e.message;
  }
  return "Unknown error";
}

/**
 * Wrap a PostgrestError in a real Error with the SAME message (no prefix) and
 * its SQLSTATE `code`, so `instanceof Error` callers and loadErrorMessage both
 * see the database's text.
 */
export function postgrestToError(error: {
  message?: string;
  code?: string;
}): Error & { code?: string } {
  const e = new Error(error.message || "unknown database error", {
    cause: error,
  }) as Error & { code?: string };
  e.code = error.code;
  return e;
}
